import { handleResponseV2, makeGet } from '@/redux/api/helpers';
import { ShippingQuote } from './shippingQuote.types';

/**
 * @category Shipping API
 * @see getItemShippingQuote
 */
type FetchItemShippingQuote = {
    authToken: string;
    countryCode: string;
    deployment: string;
    itemId: number;
    postalCode: string;
};

export type ItemShippingQuoteResponse = {
    error: boolean;
    payload: {
        itemData: {
            address1: string;
            address2: string;
            city: string;
            contactEmail: string;
            contactName: string;
            contactPhone: string;
            country: string;
            currency: string;
            depth: number;
            description: string;
            earliestPickup: number;
            fragilityType: string;
            height: number;
            image: string;
            itemID: number;
            lotNumber: string;
            note: string;
            packageType: string;
            postalCode: string;
            quoteVisibility: string;
            region: string;
            shippingDepth: number;
            shippingHeight: number;
            shippingProviderId: number;
            shippingQuantity: number;
            shippingVolumeUnit: string;
            shippingWeight: number;
            shippingWidth: number;
            subType: string;
            title: string;
            type: string;
            value: number;
            volumeUnit: string;
            weight: number;
            weightUnit: string;
            width: number;
        };
        shippingEstimate: {
            actualHandlingAndInsurance: number;
            actualTotal: number;
            carrierName: string;
            carrierService: string;
            city: string;
            currency: string;
            elabel: number;
            expirationDate: string;
            handlingAndInsurance: number;
            insuranceCost: number;
            isFlatRate: boolean;
            message: string;
            otherCosts: number;
            packagingCost: number;
            payoutToHouse: number;
            pickupCost: number;
            quoteId: string;
            quoteNumber: string;
            shippingAvailable: boolean;
            shippingProvider: number;
            shippingService: string;
            state: string;
            total: number;
            transitDays: number;
            transportationCost: number;
            visibility: string;
        };
    };
};

const transformShippingQuoteResponse = (response: ItemShippingQuoteResponse): ShippingQuote => {
    const { itemData, shippingEstimate: estimate } = response.payload;

    return {
        actualHandlingAndInsurance: estimate.actualHandlingAndInsurance,
        actualTotal: estimate.actualTotal,
        breakdown: {
            insuranceCost: estimate.insuranceCost,
            otherCost: estimate.otherCosts,
            packagingCost: estimate.packagingCost,
            payoutToHouse: estimate.payoutToHouse,
            pickupCost: estimate.pickupCost,
            transportationCost: estimate.transportationCost,
        },
        city: estimate.city,
        country: itemData.country,
        handlingAndInsurance: estimate.handlingAndInsurance,
        isFlatRate: estimate.isFlatRate,
        lots: [
            {
                crate: itemData.packageType,
                dimensions: {
                    depth: itemData.shippingDepth,
                    height: itemData.shippingHeight,
                    width: itemData.shippingWidth,
                },
                fragility: itemData.fragilityType,
                itemId: itemData.itemID,
                lotName: itemData.title,
                quantity: itemData.shippingQuantity,
                shipFrom: {
                    city: itemData.city,
                    country: itemData.country,
                    region: itemData.region,
                },
                weight: itemData.shippingWeight.toString(),
            },
        ],
        postalCode: itemData.postalCode,
        quoteId: estimate.quoteId,
        shippingCarrier: estimate.carrierName,
        shippingProgram: estimate.shippingService,
        shippingProvider: estimate.shippingProvider,
        state: estimate.state,
        total: estimate.total,
    };
};

/**
 * Fetches a shipping estimate via Get from Shipping API
 * @function fetchItemShippingQuote
 * @category Shipping API
 * @param {FetchItemShippingQuote}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/shipping/estimate
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2sprwrrwd6/resources/tztdqt/methods/GET
 * @see https://github.com/LIVEauctioneers/shipping-api/tree/master/functions/GetQuote-shipping-api
 */
export const getItemShippingQuote = ({
    authToken,
    countryCode,
    deployment,
    itemId,
    postalCode,
}: FetchItemShippingQuote) =>
    new Promise<ItemShippingQuoteResponse>((resolve, reject) => {
        const request = makeGet({
            apiPath: 'estimate',
            authToken,
            deployment,
            path: '<SHIPPING-API>',
            queryParams: { countryCode, itemId, postalCode, visibilityBypass: true },
        });
        request.end((err, response) => {
            if (response.body?.error) {
                return reject(new Error(response.body.payload));
            }

            return handleResponseV2({
                err,
                reject,
                resolve,
                response,
            });
        });
    });

export const getItemShippingQuoteNew = ({
    authToken,
    countryCode,
    deployment,
    itemId,
    postalCode,
}: FetchItemShippingQuote) =>
    new Promise<ShippingQuote>((resolve, reject) => {
        const request = makeGet({
            apiPath: 'price-check/item',
            authToken,
            deployment,
            path: '<SHIPPING-API>',
            queryParams: { countryCode, itemId, postalCode, visibilityBypass: true },
        });
        request.end((err, response) => {
            if (response.body?.error) {
                return reject(new Error(response.body.payload));
            }

            return handleResponseV2({
                err,
                reject,
                resolve,
                response,
                transform: transformShippingQuoteResponse,
            });
        });
    });

type FetchInvoiceShippingQuote = {
    authToken: string;
    deployment: string;
    invoiceId: number;
};

export type InvoiceShippingQuoteResponse = {
    error: boolean;
    payload: {
        actualHandlingAndInsurance: number;
        actualTotal: number;
        auctionHouse: string;
        auctionName: string;
        carrierName: string;
        catalogId: number;
        handlingAndInsurance: number;
        houseId: number;
        insuranceCost: number;
        isFlatRate: boolean;
        itemData: {
            crate: string;
            fragility: string;
            height: number;
            itemId: number;
            length: number;
            quantity: number;
            shipFrom: string;
            title: string;
            weight: number;
            width: number;
        }[];
        numberOfItems: number;
        otherCosts: number;
        packagingCost: number;
        payoutToHouse: number;
        pickupCost: number;
        quoteId: string;
        quoteTotal: number;
        shipToLocation: string;
        shippingPartner: string;
        shippingService: string;
        transportationCost: number;
    };
};

const transformInvoiceShippingQuoteResponse = (response: InvoiceShippingQuoteResponse): ShippingQuote => {
    const { payload } = response;

    return {
        actualHandlingAndInsurance: payload.actualHandlingAndInsurance,
        actualTotal: payload.actualTotal,
        auctionDetails: {
            catalogId: payload.catalogId,
            house: payload.auctionHouse,
            houseId: payload.houseId,
            name: payload.auctionName,
        },
        breakdown: {
            insuranceCost: payload.insuranceCost,
            otherCost: payload.otherCosts,
            packagingCost: payload.packagingCost,
            payoutToHouse: payload.payoutToHouse,
            pickupCost: payload.pickupCost,
            transportationCost: payload.transportationCost,
        },
        handlingAndInsurance: payload.handlingAndInsurance,
        isFlatRate: payload.isFlatRate,
        lots: payload.itemData.map((item) => ({
            crate: item.crate,
            dimensions: {
                depth: item.length,
                height: item.height,
                width: item.width,
            },
            fragility: item.fragility,
            itemId: item.itemId,
            lotName: item.title,
            quantity: item.quantity,
            shipFrom: item.shipFrom,
            weight: item.weight.toString(),
        })),
        quoteId: payload.quoteId,
        shippingCarrier: payload.carrierName,
        shippingProgram: payload.shippingService,
        shippingProvider: payload.shippingPartner,
        shipToLocation: payload.shipToLocation,
        total: payload.quoteTotal,
    };
};

export const getInvoiceShippingQuote = ({ authToken, deployment, invoiceId }: FetchInvoiceShippingQuote) =>
    new Promise<ShippingQuote>((resolve, reject) => {
        const request = makeGet({
            apiPath: 'price-check/invoice',
            authToken,
            deployment,
            path: '<SHIPPING-API>',
            queryParams: { invoiceid: invoiceId },
        });
        request.end((err, response) => {
            if (response.body?.error) {
                return reject(new Error(response.body.payload));
            }

            return handleResponseV2({
                err,
                reject,
                resolve,
                response,
                transform: transformInvoiceShippingQuoteResponse,
            });
        });
    });
